import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { asBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import IUrl, { ISonURL } from '../../types/IUrl';
import { ProfileImageSmall } from '../../types/api/ImageType';
import { Table, TableBody, TableCell, TableHead, TableModifier, TableRow } from '../../components/organisms/Table';
import { Pagination, PaginationItemWithPageLink } from '../../_lib/components/Pagination';
import ContentMessage from '../../_lib/components/ContentMessage/ContentMessage';
import { ProfilePictureModifier, ProfilePictureName } from '../../_lib/components/ProfilePictureName';
import { AttributeRule } from '../../types/api/AttributeGroups';
import { PersonAttributes } from '../../types/api/PersonAttributes';

type AttributeGroupPerson = {
  id: string;
  personInfoCardUrl: IUrl;
  profileName: string;
  profileLink: ISonURL;
  profileImageUrl: ProfileImageSmall;
  values?: Partial<Record<PersonAttributes, string>>;
};

type AttributeGroupPersonsBlockProps = {
  persons: AttributeGroupPerson[];
  pageItems?: PaginationItemWithPageLink[];
  rules: AttributeRule[];
};

type DistinctRule = {
  attributeName: string;
  conditionValue: string[];
};

/**
 * Table of members of an Attribute Group.
 */
export function AttributeGroupPersons({ persons, rules, pageItems }: AttributeGroupPersonsBlockProps) {
  const { t } = useTranslation('AttributeGroups');

  if (persons?.length === 0) {
    return <ContentMessage text={t('table.noPersonsFound')} />;
  }

  // multiple rules of same attributeName possible, need to combine them
  const setOfAttributeNames = new Set(rules.map(rule => rule.attributeName));
  const distinctRules: DistinctRule[] = Array.from(setOfAttributeNames).map(name => ({
    attributeName: name,
    conditionValue: rules.filter(rule => rule.attributeName === name).map(rule => rule.conditionValue),
  }));

  /**
   * Retrieves all attributes in a member that are part of the rules and maps them to a TableCell.
   */
  const mapRuleAttributeNameFromMember = (member: AttributeGroupPerson): React.JSX.Element[] => {
    return distinctRules.map(rule => (
      <TableCell key={member.id + rule.attributeName} dataHead={t(`PersonAttributes:${rule.attributeName}`)}>
        {member.values?.[rule.attributeName as keyof typeof member.values]}
      </TableCell>
    ));
  };

  return (
    <>
      <Table caption={t('table.caption')} captionHidden>
        <TableHead>
          <TableRow>
            <TableCell modifier={TableModifier.MWIDTH}>Name</TableCell>
            {distinctRules.map(rule => (
              <TableCell key={rule.attributeName}>{t(`PersonAttributes:${rule.attributeName}`)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {persons.map(person => (
            <TableRow key={person.id}>
              <TableCell dataHead={t('table.name')}>
                <ProfilePictureName {...person} modifier={ProfilePictureModifier.ALIGN_TOP} />
              </TableCell>
              {mapRuleAttributeNameFromMember(person)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination pageItems={pageItems} />
    </>
  );
}

export const AttributeGroupPersonsBlock = asBlock(AttributeGroupPersons, BlockType.attributegrouppersons);
export type AttributeGroupPersonsBlock = ComponentProps<typeof AttributeGroupPersonsBlock>;
